import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Skeleton,
  Stack,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import BookmarkAddedRoundedIcon from "@mui/icons-material/BookmarkAddedRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ProductSelectionModal from "../components/client/modal/ProductSelectionModal";
import api from "../services/api";
import { withCountryPrefix, withQuery } from "../components/utils/routeHelpers";

function toTs(value) {
  return value ? new Date(value).getTime() : 0;
}

function price(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toFixed(2) : "--";
}

function getCurrencySymbol(routeCountry, item) {
  return Number(item?.country_id) === 2 || String(routeCountry).toLowerCase() === "us" ? "$" : "€";
}

function humanizeDate(value) {
  if (!value) return "No recent change";
  const diff = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(diff) || diff < 0) return new Date(value).toLocaleDateString();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes || 1} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(value).toLocaleDateString();
}

function getChangeMeta(percent) {
  const value = Number(percent);
  if (!Number.isFinite(value) || value === 0) {
    return {
      color: "default",
      icon: <RemoveRoundedIcon sx={{ fontSize: 16 }} />,
      label: "No change",
    };
  }
  if (value < 0) {
    return {
      color: "success",
      icon: <ArrowDownwardRoundedIcon sx={{ fontSize: 16 }} />,
      label: `${Math.abs(value).toFixed(2)}% drop`,
    };
  }
  return {
    color: "error",
    icon: <ArrowUpwardRoundedIcon sx={{ fontSize: 16 }} />,
    label: `${value.toFixed(2)}% increase`,
  };
}

function getStoreLabel(item, fallback = "Store unavailable") {
  return [item?.store_chain, item?.store_name].filter(Boolean).join(" • ") || item?.store_label || fallback;
}

function getPackageText(item) {
  return (
    item?.package_size_text ||
    (item?.package_size_value && item?.package_size_unit
      ? `${item.package_size_value} ${item.package_size_unit}`
      : null)
  );
}

function formatUnitPrice(item, currencySymbol) {
  const value = Number(item?.unit_price);
  if (!Number.isFinite(value) || value <= 0) return null;
  const kind = String(item?.unit_price_kind || "").toLowerCase();
  if (kind === "piece") return `${currencySymbol}${value.toFixed(2)} / τεμ.`;
  if (kind === "liter" || kind === "l") return `${currencySymbol}${value.toFixed(2)} / L`;
  return `${currencySymbol}${value.toFixed(2)} / kg`;
}

function mergeWatchlistItems(items, nextItem, replacedItemId) {
  if (!nextItem?.id) {
    return replacedItemId ? items.filter((entry) => entry.id !== replacedItemId) : items;
  }
  const filtered = items.filter(
    (entry) =>
      entry.id !== replacedItemId &&
      entry.id !== nextItem.id &&
      entry.variant_id !== nextItem.variant_id
  );
  return [nextItem, ...filtered];
}

export default function FollowedProductsPage() {
  const { t } = useTranslation();
  const { country = "el" } = useParams();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [watchlistMeta, setWatchlistMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState("recent_change");

  const [selectionOpen, setSelectionOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [genericProducts, setGenericProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedGenericId, setSelectedGenericId] = useState("");
  const [loadingGenericProducts, setLoadingGenericProducts] = useState(false);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [submittingSelection, setSubmittingSelection] = useState(false);
  const [selectionError, setSelectionError] = useState("");

  const plansPath = withQuery(withCountryPrefix("/plans", country), { intent: "premium", plan: "monthly" });
  const recipesPath = withCountryPrefix("/search-recipes", country);

  const loadItems = useCallback(() => {
    setLoading(true);
    return api.price
      .getWatchlist()
      .then((res) => {
        setItems(Array.isArray(res?.data?.items) ? res.data.items : []);
        setWatchlistMeta(res?.data?.meta || {});
        setError("");
      })
      .catch((err) => {
        const status = err?.response?.status;
        setError(
          (status === 401
            ? (t("login_required_title") || "Please log in to continue.")
            : status === 403
              ? (err?.response?.data?.message || t("premium_feature_title") || "Premium feature")
              : null) ||
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          t("followed_products_load_error") ||
          "Unable to load followed products."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [t]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const sortedItems = useMemo(() => {
    const next = [...items];
    next.sort((a, b) => {
      if (sortBy === "recent_change") return toTs(b.price_changed_at) - toTs(a.price_changed_at);
      if (sortBy === "largest_drop") return Number(a.change_percent || a.delta_pct || 0) - Number(b.change_percent || b.delta_pct || 0);
      if (sortBy === "largest_increase") return Number(b.change_percent || b.delta_pct || 0) - Number(a.change_percent || a.delta_pct || 0);
      return String(a.generic_product_name || a.name || "").localeCompare(String(b.generic_product_name || b.name || ""));
    });
    return next;
  }, [items, sortBy]);

  const summary = useMemo(() => {
    const recent = items.filter((item) => {
      const ts = item?.price_changed_at ? new Date(item.price_changed_at).getTime() : 0;
      return ts > 0 && Date.now() - ts <= 7 * 24 * 60 * 60 * 1000;
    }).length;
    return {
      followed: items.length,
      recent,
    };
  }, [items]);

  const loadGenericProducts = useCallback(async (query = "") => {
    setLoadingGenericProducts(true);
    setSelectionError("");
    try {
      const res = await api.shoppingList.getSelectionGenericProducts({ q: query || undefined });
      const nextProducts = Array.isArray(res?.data?.items) ? res.data.items : [];
      setGenericProducts(nextProducts);
      return nextProducts;
    } catch (err) {
      setSelectionError(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (t("generic_products_load_error") || "Unable to load products right now.")
      );
      return [];
    } finally {
      setLoadingGenericProducts(false);
    }
  }, [t]);

  const loadVariants = useCallback(async (genericProductId) => {
    if (!genericProductId) {
      setVariants([]);
      return [];
    }
    setLoadingVariants(true);
    setSelectionError("");
    try {
      const res = await api.shoppingList.getSelectionVariants(genericProductId);
      const nextVariants = Array.isArray(res?.data?.items) ? res.data.items : [];
      setVariants(nextVariants);
      return nextVariants;
    } catch (err) {
      setSelectionError(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (t("variant_products_load_error") || "Unable to load store variants right now.")
      );
      setVariants([]);
      return [];
    } finally {
      setLoadingVariants(false);
    }
  }, [t]);

  const openSelectionModal = useCallback(async (item = null) => {
    setEditingItem(item);
    setSelectionOpen(true);
    setSearchValue("");
    setSelectionError("");
    const genericId = item?.generic_product_id ? String(item.generic_product_id) : "";
    setSelectedGenericId(genericId);
    await loadGenericProducts("");
    if (genericId) {
      await loadVariants(genericId);
    } else {
      setVariants([]);
    }
  }, [loadGenericProducts, loadVariants]);

  const closeSelectionModal = useCallback(() => {
    if (submittingSelection) return;
    setSelectionOpen(false);
    setEditingItem(null);
    setSearchValue("");
    setSelectedGenericId("");
    setVariants([]);
    setSelectionError("");
  }, [submittingSelection]);

  const handleSearchChange = useCallback(async (query) => {
    setSearchValue(query);
    await loadGenericProducts(query);
  }, [loadGenericProducts]);

  const handleSelectGeneric = useCallback(async (genericProductId) => {
    setSelectedGenericId(String(genericProductId));
    await loadVariants(genericProductId);
  }, [loadVariants]);

  const handleSelectVariant = useCallback(async (variantId) => {
    if (!variantId) return;
    setSubmittingSelection(true);
    setSelectionError("");
    const previousItems = items;
    const selectedVariant = variants.find(
      (entry) => String(entry?.id || entry?.product_variant_id) === String(variantId)
    );
    const selectedGeneric = genericProducts.find(
      (entry) => String(entry?.id) === String(selectedGenericId)
    );
    const optimisticItem = editingItem ? null : {
      id: `pending-${variantId}`,
      variant_id: variantId,
      generic_product_id: selectedGenericId ? Number(selectedGenericId) : null,
      generic_product_name:
        selectedGeneric?.name ||
        selectedGeneric?.name_el ||
        selectedGeneric?.name_en ||
        "",
      name:
        selectedVariant?.brand_name ||
        selectedVariant?.title ||
        selectedVariant?.name ||
        "",
      price: selectedVariant?.price ?? selectedVariant?.package_price ?? null,
      package_size_text: selectedVariant?.package_size_text || null,
      store_name: selectedVariant?.store_name || null,
      store_chain: selectedVariant?.store_chain || null,
      unit_price: selectedVariant?.unit_price ?? null,
      unit_price_kind: selectedVariant?.unit_price_kind ?? null,
      price_changed_at: null,
      price_was: null,
      change_percent: null,
      delta_pct: null,
    };
    try {
      if (optimisticItem) {
        setItems((current) => mergeWatchlistItems(current, optimisticItem, null));
      }
      if (editingItem?.variant_id && String(editingItem.variant_id) !== String(variantId)) {
        await api.price.updateWatchlist({
          variant_id: editingItem.variant_id,
          action: "remove",
        });
      }

      const res = await api.price.updateWatchlist({ variant_id: variantId });
      const updatedItem = res?.data?.item || null;
      setItems((current) => mergeWatchlistItems(current, updatedItem, editingItem?.id));
      closeSelectionModal();
      await loadItems();
    } catch (err) {
      setItems(previousItems);
      setSelectionError(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (t("watchlist_add_error") || "Failed to update watchlist.")
      );
    } finally {
      setSubmittingSelection(false);
    }
  }, [closeSelectionModal, editingItem, genericProducts, items, loadItems, selectedGenericId, t, variants]);

  const handleUnfollow = useCallback(async (item) => {
    const previous = items;
    setItems((current) => current.filter((entry) => entry.id !== item.id));
    try {
      await api.price.updateWatchlist({
        variant_id: item.variant_id,
        action: "remove",
      });
      await loadItems();
    } catch (err) {
      setItems(previous);
      setError(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        t("followed_products_remove_error") ||
        "Unable to remove followed product."
      );
    }
  }, [items, loadItems, t]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={2.5}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid",
            borderColor: "divider",
            background: (theme) => theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(73,188,144,0.12), rgba(20,22,26,0.95))"
              : "linear-gradient(135deg, rgba(0,158,117,0.08), rgba(255,255,255,0.96))",
            boxShadow: (theme) => theme.palette.mode === "dark"
              ? "0 16px 42px rgba(0,0,0,0.32)"
              : "0 16px 38px rgba(0,0,0,0.08)",
          }}
        >
          <CardContent>
            <Stack spacing={2}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: { xs: "flex-start", md: "center" }, flexDirection: { xs: "column", md: "row" } }}>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <BookmarkAddedRoundedIcon color="success" />
                    <Chip size="small" color="warning" variant="outlined" label={t("premium_required") || "Premium"} />
                  </Stack>
                  <Typography variant="h4" fontWeight={800}>
                    {t("price_watch_title") || "Price Watch"}
                  </Typography>
                  <Typography color="text.secondary">
                    {t("price_watch_subtitle") || "Track your followed products, review the latest price movement, and act before the next increase."}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip color="success" variant="filled" label={`${summary.followed} ${t("followed_items") || "followed"}`} />
                  <Chip color={summary.recent > 0 ? "warning" : "default"} variant="outlined" label={`${summary.recent} ${t("recent_changes") || "recent changes"}`} />
                  {watchlistMeta?.limit != null ? (
                    <Chip variant="outlined" label={`${watchlistMeta.count || 0}/${watchlistMeta.limit}`} />
                  ) : null}
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: { xs: "flex-start", md: "center" }, flexDirection: { xs: "column", md: "row" } }}>
          <Box>
            <Typography variant="h6" fontWeight={800}>
              {t("price_watch_title") || "Price Watch"}
            </Typography>
            <Typography color="text.secondary">
              {t("price_watch_sort_hint") || "Sort by movement, scan the best drops, and remove products when you are done."}
            </Typography>
          </Box>
          <TextField
            select
            size="small"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="recent_change">{t("sort_recent_change") || "Most recently changed"}</MenuItem>
            <MenuItem value="largest_drop">{t("sort_largest_drop") || "Biggest drop %"}</MenuItem>
            <MenuItem value="largest_increase">{t("sort_largest_increase") || "Biggest increase %"}</MenuItem>
            <MenuItem value="alphabetical">{t("sort_alphabetical") || "Alphabetical"}</MenuItem>
          </TextField>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={() => openSelectionModal()}
            sx={{
              borderRadius: 999,
              px: 2.25,
              py: 1,
              fontWeight: 800,
              boxShadow: (theme) => theme.palette.mode === "dark"
                ? "0 10px 24px rgba(73,188,144,0.25)"
                : "0 10px 22px rgba(0,158,117,0.18)",
            }}
          >
            {t("add_watch_button") || "+ Παρακολούθηση προϊόντος"}
          </Button>
        </Box>

        {error ? (
          <Alert
            severity="warning"
            action={
              error.toLowerCase().includes("premium") ? (
                <Button color="inherit" size="small" onClick={() => navigate(plansPath)}>
                  {t("upgrade_to_premium") || "Upgrade"}
                </Button>
              ) : (
                <Button color="inherit" size="small" onClick={loadItems}>
                  {t("retry") || "Retry"}
                </Button>
              )
            }
          >
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Stack spacing={2}>
            {[0, 1, 2].map((idx) => (
              <Card key={idx} variant="outlined" sx={{ borderRadius: 4 }}>
                <CardContent>
                  <Stack spacing={1.2}>
                    <Skeleton variant="text" width="38%" height={36} />
                    <Skeleton variant="text" width="28%" />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                      <Skeleton variant="rounded" width={120} height={28} />
                      <Skeleton variant="rounded" width={120} height={28} />
                      <Skeleton variant="rounded" width={130} height={28} />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : sortedItems.length === 0 ? (
          <Card variant="outlined" sx={{ borderRadius: 4 }}>
            <CardContent>
              <Stack spacing={1.5} alignItems="flex-start">
                <Chip color="default" variant="outlined" label={t("empty_state") || "Empty"} />
                <Typography variant="h6" fontWeight={800}>
                  {t("followed_products_empty_title") || "You’re not following any products yet"}
                </Typography>
                <Typography color="text.secondary">
                  {t("followed_products_empty") || "Tap Παρακολούθηση τιμής from recipes or shopping lists to build your Premium watchlist."}
                </Typography>
                <Button variant="contained" onClick={() => navigate(recipesPath)}>
                  {t("browse_recipes") || "Browse recipes"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2}>
            {sortedItems.map((item) => {
              const currencySymbol = getCurrencySymbol(country, item);
              const packageText = getPackageText(item);
              const unitPriceText = formatUnitPrice(item, currencySymbol);
              const changeMeta = getChangeMeta(item.change_percent ?? item.delta_pct);
              const title = item.generic_product_name || item.name || `Product #${item.variant_id}`;
              return (
                <Card
                  key={item.id}
                  variant="outlined"
                  sx={{
                    borderRadius: 4,
                    boxShadow: (theme) => theme.palette.mode === "dark"
                      ? "0 10px 24px rgba(0,0,0,0.28)"
                      : "0 8px 22px rgba(0,0,0,0.06)",
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.2}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                        <Box>
                          <Typography fontWeight={800}>{title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>{t("best_offer_label") || "Best offer"}:</strong> {getStoreLabel(item)}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip size="small" color={changeMeta.color} icon={changeMeta.icon} label={changeMeta.label} />
                          <Button size="small" variant="outlined" onClick={() => openSelectionModal(item)}>
                            {t("change_watch_product") || "Αλλαγή προϊόντος"}
                          </Button>
                          <Button size="small" color="error" onClick={() => handleUnfollow(item)}>
                            {t("unfollow_price") || "Unfollow"}
                          </Button>
                        </Stack>
                      </Box>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap">
                        <Typography variant="body2"><strong>{t("current_price") || "Current"}:</strong> {price(item.price)}{currencySymbol}</Typography>
                        {packageText ? (
                          <Typography variant="body2"><strong>{t("package_size") || "Package"}:</strong> {packageText}</Typography>
                        ) : null}
                        {unitPriceText ? (
                          <Typography variant="body2"><strong>{t("unit_price") || "Unit price"}:</strong> {unitPriceText}</Typography>
                        ) : null}
                        <Typography variant="body2"><strong>{t("price_was") || "Was"}:</strong> {price(item.price_was)}{currencySymbol}</Typography>
                        <Typography variant="body2"><strong>{t("last_change") || "Last change"}:</strong> {humanizeDate(item.price_changed_at)}</Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </Stack>

      <ProductSelectionModal
        open={selectionOpen}
        onClose={closeSelectionModal}
        genericProducts={genericProducts}
        variants={variants}
        selectedGenericId={selectedGenericId}
        currentVariantId={editingItem?.variant_id || ""}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        onSelectGeneric={handleSelectGeneric}
        onSelectVariant={handleSelectVariant}
        loadingGenericProducts={loadingGenericProducts}
        loadingVariants={loadingVariants}
        submittingSelection={submittingSelection}
        error={selectionError}
        title={editingItem ? (t("change_watch_product") || "Αλλαγή προϊόντος") : (t("add_watch_button") || "+ Παρακολούθηση προϊόντος")}
        emptyProductsLabel={t("no_generic_products_found") || "No generic products found."}
        emptyVariantsLabel={t("no_variants_found") || "No store variants found for this product."}
      />
    </Container>
  );
}
